import React from "react";
import { List, Space, Tag, Tooltip } from "antd";
import { UserRoundPen, UserRoundX } from "lucide-react";
import moment from "moment";
import useBreakpoint from "hooks/useBreakpoint";

const LIST_PAGE_SIZE = 3;

function getDropColor(value) {
  if (value < -10) return "red";
  if (value <= -6 && value >= -10) return "yellow";
  return "green";
}

function getWellBeingColor(value) {
  if (value < 1.5) return "red";
  if (value < 2.5) return "orange";
  if (value < 3.5) return "yellow";
  if (value < 4.5) return "blue";
  return "green";
}

function useRecordListPagination(total) {
  const { isMobile } = useBreakpoint();

  if (total <= LIST_PAGE_SIZE) {
    return false;
  }

  return {
    pageSize: LIST_PAGE_SIZE,
    showSizeChanger: false,
    size: "small",
    ...(isMobile ? { simple: true } : {}),
  };
}

function RecordActions({ row, editControl, deleteControl }) {
  return (
    <Space size={12}>
      <Tooltip title="Editar Controle">
        <UserRoundPen
          onClick={() => editControl(row)}
          size={20}
          style={{ cursor: "pointer", color: "#09ac9c" }}
        />
      </Tooltip>
      <Tooltip title="Deletar Controle">
        <UserRoundX
          onClick={() => deleteControl(row)}
          size={20}
          style={{ cursor: "pointer", color: "#EC6D62" }}
        />
      </Tooltip>
    </Space>
  );
}

function RecordHeader({ row, editControl, deleteControl }) {
  const typeTraining = row.typeTraining ?? "A";
  const color = typeTraining === "B" ? "geekblue" : "green";
  const dateLabel = row.date ? moment(row.date).format("DD/MM/YYYY") : "—";

  return (
    <div className="load-control-record-header">
      <div className="load-control-record-header-main">
        <Tag color={color} style={{ margin: 0 }}>
          Sem. {typeTraining}
        </Tag>
        <span className="load-control-record-date">{dateLabel}</span>
      </div>
      <RecordActions row={row} editControl={editControl} deleteControl={deleteControl} />
    </div>
  );
}

function MetricCell({ label, value }) {
  return (
    <div className="load-control-record-metric">
      <span className="load-control-record-metric-label">{label}</span>
      <span className="load-control-record-metric-value">{value ?? "—"}</span>
    </div>
  );
}

export function LoadControlCmjCompactList({
  dataSource,
  editControl,
  deleteControl,
  locale,
}) {
  const pagination = useRecordListPagination(dataSource?.length ?? 0);

  if (!dataSource?.length) {
    return (
      <div className="load-control-record-empty">
        {locale?.emptyText ?? "Sem registro"}
      </div>
    );
  }

  return (
    <List
      className="load-control-compact-list"
      dataSource={dataSource}
      rowKey="id"
      pagination={pagination}
      renderItem={(row) => (
        <List.Item style={{ padding: "6px 0", border: "none" }}>
          <div className="load-control-record-card">
            <RecordHeader
              row={row}
              editControl={editControl}
              deleteControl={deleteControl}
            />
            <div className="load-control-record-grid load-control-record-grid--cmj">
              <MetricCell label="CMJ PRÉ" value={row.cmjFirst} />
              <div className="load-control-record-metric">
                <span className="load-control-record-metric-label">Queda PRÉ</span>
                <Tag color={getDropColor(row.cmjFirstDrop)} style={{ margin: 0 }}>
                  {row.cmjFirstDrop ?? "—"}
                </Tag>
              </div>
              <MetricCell label="CMJ PÓS" value={row.cmjSecond} />
              <div className="load-control-record-metric">
                <span className="load-control-record-metric-label">Queda PÓS</span>
                <Tag color={getDropColor(row.cmjSecondDrop)} style={{ margin: 0 }}>
                  {row.cmjSecondDrop ?? "—"}
                </Tag>
              </div>
            </div>
          </div>
        </List.Item>
      )}
    />
  );
}

export function LoadControlWellBeingCompactList({
  dataSource,
  editControl,
  deleteControl,
  locale,
}) {
  const pagination = useRecordListPagination(dataSource?.length ?? 0);

  if (!dataSource?.length) {
    return (
      <div className="load-control-record-empty">
        {locale?.emptyText ?? "Sem registro"}
      </div>
    );
  }

  return (
    <List
      className="load-control-compact-list"
      dataSource={dataSource}
      rowKey="id"
      pagination={pagination}
      renderItem={(row) => (
        <List.Item style={{ padding: "6px 0", border: "none" }}>
          <div className="load-control-record-card">
            <RecordHeader
              row={row}
              editControl={editControl}
              deleteControl={deleteControl}
            />
            <div className="load-control-record-grid load-control-record-grid--wellbeing">
              <MetricCell label="Fadiga" value={row.fatigue} />
              <MetricCell label="Dor" value={row.pain} />
              <MetricCell label="Humor" value={row.humor} />
              <MetricCell label="Estresse" value={row.stress} />
              <MetricCell label="Sono" value={row.sleep} />
              <div className="load-control-record-metric">
                <span className="load-control-record-metric-label">Média</span>
                <Tag
                  color={getWellBeingColor(row.averageWellBeing)}
                  style={{ margin: 0 }}
                >
                  {row.averageWellBeing ?? "—"}
                </Tag>
              </div>
            </div>
          </div>
        </List.Item>
      )}
    />
  );
}
